import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TFunction } from 'react-i18next';
import { ButtonVariant } from 'web-library/stories/atom/Button/Button';
import { MutationStatus } from 'react-query';
import PaymentErrorModal from '../PaymentErrorModal';
import { useCustomMutate } from '../../../../hooks/query';
import { postPaymentOffline } from '../../../../api/financial/api';
import { useENetsWindowEventListener, usePayNowPolling } from '../../logic/usePaymentButton';
import QRPaymentModal from '../QRPaymentModal';
import ENetsModal from '../ENetsModal';
import { useUserDetails } from '../../../../hooks/userDetails';
import { useCoordinatorResit } from '../../../resit/logic/useCoordinatorResit';
import RouteKey from '../../../../routes/key';
import { PaymentMethod } from './PaymentApiButton';
import { ExamPaymentModeKey } from '../../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';
import SelfPayMethodsModal from './SelfPayMethodsModal';
import PaymentModals from './PaymentModals';
import { ApplicationType } from '../../../../api/candidate/config/model';

/**
 * THIS FILE IS MEANT FOR GROUP EXAM REGISTRATION PAYMENT FLOW.
 * IF ALL OTHER FLOWS INVOLVING PAYMENT IN THE FUTURE NEED TO SWITCH TO
 * SIMILIAR FLOW AND USE THIS COMPONENT AS WELL, PLS RENAME THIS
 * FOLDER/FILE/COMPONENT TO GENERIC NAME
 */
const { coordinatorExamRegistrations } = RouteKey.exam.child;
const { amendment, exam } = RouteKey.child;

export const SET_ERROR_ACTION = 'SET_ERROR_ACTION';
export const RESET_ERROR_ACTION = 'RESET_ERROR_ACTION';
export const REST_ERROR_WITH_RETRY_ACTION = 'REST_ERROR_WITH_RETRY_ACTION';

interface PaymentErrorState {
  isErrorModalOpen: boolean;
  isRetry?: boolean;
  errorMessages?: string[];
}

interface SetErrorAction {
  type: typeof SET_ERROR_ACTION;
  payload: PaymentErrorState;
}

interface ResetErrorAction {
  type: typeof RESET_ERROR_ACTION;
}

interface ResetErrorActionWithRetry {
  type: typeof REST_ERROR_WITH_RETRY_ACTION;
}

export type PaymentErrorAction = SetErrorAction | ResetErrorAction | ResetErrorActionWithRetry;

interface PaymentButtonProps {
  onHandlePaymentMethod: (callback: (txnId: string) => void, paymentMethod?: string) => void;
  onClickPaymentButton: (callback: (hasConfirmModal?: boolean) => void) => void;
  onSuccessPayment: (txnId: string) => void;
  selfPayMethodsAvailable?: Array<PaymentMethod>;
  isUniqueLink: boolean;
  disabled?: boolean;
  onPaymentComplete?: (retry: boolean) => void;
  buttonText?: string;
  buttonErrorText?: string;
  fullWidth?: boolean;
  buttonVariant?: ButtonVariant;
  customConfirmationModalContent?: TFunction<'translation'>;
  coordinatorExamPaymentMode?: ExamPaymentModeKey;
  zeroDollarPaymentOption: boolean;
  statusPaymentOffline: MutationStatus;
  applicationType?: ApplicationType;
}

export const PAYMENT_ERROR_INITIAL_STATE: PaymentErrorState = {
  isErrorModalOpen: false,
  isRetry: undefined,
  errorMessages: undefined,
};

export const paymentErrorReducer = (
  state: PaymentErrorState,
  action: PaymentErrorAction
): PaymentErrorState => {
  switch (action.type) {
    case SET_ERROR_ACTION:
      return { ...state, ...action.payload };
    case REST_ERROR_WITH_RETRY_ACTION:
      return { ...state, isRetry: false };
    case RESET_ERROR_ACTION:
    default:
      return PAYMENT_ERROR_INITIAL_STATE;
  }
};

const UNEXPECTED_ERROR_ACTION: PaymentErrorAction = {
  type: SET_ERROR_ACTION,
  payload: {
    isErrorModalOpen: true,
    isRetry: true,
    errorMessages: ['An unexpected error occurred. Please try again.'],
  },
};

const isApplicationAmendment = (applicationType?: ApplicationType) =>
  applicationType && applicationType === ApplicationType.AMENDMENT;

const NoRetryStatusHandling = (
  userDetails: any,
  stepNavigate: any,
  history: any,
  retry: boolean,
  applicationType?: ApplicationType
) => {
  if (!retry && userDetails && userDetails.data) {
    history.push(`/${exam}/${coordinatorExamRegistrations.route}`);
  }
  if (isApplicationAmendment(applicationType)) {
    history.push(`/${amendment}/${coordinatorExamRegistrations.route}`);
  }
};

const openCheckoutWindow = (internalTransactionRef: string): Window | null =>
  window.open(
    `${import.meta.env.VITE_APP_PAYMENT_REDIRECT_DOMAIN}/financial/api/nets/checkout?paymentMode=NETS_CC&internalTransactionRef=${internalTransactionRef}`,
    'checkout',
    'menubar=no,toolbar=no,scrollbars=yes'
  );

/**
 * Do not unmount this component when payment is ongoing
 *
 * @param onClickPaymentButton
 * @param onSuccessPayment
 * @param handlePaymentMethod
 * @param paymentMethodsAvailable
 * @param disabled
 */
const GroupExamRegistrationPaymentButton = ({
  onClickPaymentButton = (callback) => {
    callback(false);
  },
  onHandlePaymentMethod = (callback) => {
    callback('');
  },
  onSuccessPayment,
  selfPayMethodsAvailable = [PaymentMethod.ENETS_CREDIT_CARD, PaymentMethod.PAYNOW_QR],
  disabled = undefined,
  isUniqueLink = false,
  onPaymentComplete = (retry: boolean) => null,
  buttonText,
  fullWidth = true,
  buttonVariant,
  customConfirmationModalContent,
  coordinatorExamPaymentMode,
  zeroDollarPaymentOption = false,
  buttonErrorText = 'Back To Registered Exam(s)',
  statusPaymentOffline,
  applicationType,
}: PaymentButtonProps) => {
  const { data: userDetails } = useUserDetails();
  const history = useHistory();
  const { stepNavigate } = useCoordinatorResit(false);

  const [internalTransactionRef, setInternalTransactionRef] = useState('');

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [isENetsModalOpen, setIsENetsModalOpen] = useState(false);
  const [isQRPaymentModalOpen, setIsQRPaymentModalOpen] = useState(false);

  const [{ isErrorModalOpen, isRetry, errorMessages }, paymentErrorDispatch] = useReducer(
    paymentErrorReducer,
    PAYMENT_ERROR_INITIAL_STATE
  );

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [isDisabled, setIsDisabled] = useState(false);
  // get a reference to opened enets window, so can refocus to that window if it exists
  const [enetsWindowObjectReference, setEnetsWindowObjectReference] = useState<Window | null>(null);

  useEffect(() => {
    // open a new window instead of refocusing previous window when internalTransactionRef changed
    // because it should be treated as a different transaction
    setEnetsWindowObjectReference(null);
  }, [internalTransactionRef]);

  useEffect(() => {
    const checkChildWindowClosed = (windowObject: any) => {
      if (windowObject && windowObject.closed) {
        setIsENetsModalOpen(false);
        clearInterval(pollChildWindow);
      }
    };
    const pollChildWindow = setInterval(
      () => checkChildWindowClosed(enetsWindowObjectReference),
      200
    );

    return () => clearInterval(pollChildWindow);
  }, [enetsWindowObjectReference]);

  // listen to data sent from ENetsPaymentInterimResult.tsx
  useENetsWindowEventListener(
    selectedPaymentMethod,
    internalTransactionRef,
    paymentErrorDispatch,
    onSuccessPayment,
    setIsPaymentModalOpen
  );

  const [pollingEnabled, setPollingEnabled] = useState(false);

  const { useQrCodeMutate, regenerateQrCode, countDownDisplay, isQrExpired } = usePayNowPolling(
    selectedPaymentMethod,
    internalTransactionRef,
    pollingEnabled,
    isQRPaymentModalOpen,
    (value) => setPollingEnabled(value),
    () => onSuccessPayment(internalTransactionRef)
  );

  const { mutate: offlinePaymentMutate } = useCustomMutate(
    'POST_MANUAL_BANK_TRANSFER',
    postPaymentOffline
  );
  const closePaymentModals = () => {
    setIsENetsModalOpen(false);
    setIsQRPaymentModalOpen(false);
    setIsPaymentModalOpen(false);
    setIsConfirmationModalOpen(false);
  };

  const closeAllPaymentModals = useCallback(
    (retry: boolean) => {
      closePaymentModals();
      NoRetryStatusHandling(userDetails, stepNavigate, history, retry, applicationType);
      paymentErrorDispatch({ type: RESET_ERROR_ACTION });
    },
    [applicationType, history, stepNavigate, userDetails]
  );

  const paymentModalOnClose = (paymentMethod: PaymentMethod) => {
    if (paymentMethod === PaymentMethod.ENETS_CREDIT_CARD) {
      setIsENetsModalOpen(false);
      setInternalTransactionRef('');
    } else if (paymentMethod === PaymentMethod.PAYNOW_QR) {
      setIsQRPaymentModalOpen(false);
      setPollingEnabled(false);
    }
    setIsDisabled(false);
    setIsPaymentModalOpen(true);
    setSelectedPaymentMethod(null);
  };
  return (
    <>
      <SelfPayMethodsModal
        selfPayMethodsAvailable={selfPayMethodsAvailable}
        isDisabled={isDisabled}
        modelOpen={isPaymentModalOpen}
        onClose={() => {
          setIsDisabled(false);
          setIsPaymentModalOpen(false);
        }}
        onClickCheckout={(paymentMethod) => {
          const onClickPaymentCallback = (txnId: string) => {
            if (txnId) {
              setInternalTransactionRef(txnId);
              setIsPaymentModalOpen(false);
              setSelectedPaymentMethod(paymentMethod);
              setIsENetsModalOpen(PaymentMethod.ENETS_CREDIT_CARD === paymentMethod);
              setIsQRPaymentModalOpen(PaymentMethod.PAYNOW_QR === paymentMethod);
              if (!(paymentMethod in PaymentMethod)) {
                paymentErrorDispatch(UNEXPECTED_ERROR_ACTION);
              }
            } else {
              paymentErrorDispatch(UNEXPECTED_ERROR_ACTION);
            }
          };
          setIsDisabled(true);
          onHandlePaymentMethod(onClickPaymentCallback, paymentMethod);
        }}
      />

      <ENetsModal
        modelOpen={isENetsModalOpen}
        onClose={() => paymentModalOnClose(PaymentMethod.ENETS_CREDIT_CARD)}
        onClickCheckout={() => {
          if (!enetsWindowObjectReference || enetsWindowObjectReference.closed) {
            const windowObject = openCheckoutWindow(internalTransactionRef);
            setEnetsWindowObjectReference(windowObject);
          } else {
            enetsWindowObjectReference.focus();
          }
        }}
      />

      <QRPaymentModal
        modelOpen={isQRPaymentModalOpen}
        onClose={() => paymentModalOnClose(PaymentMethod.PAYNOW_QR)}
        useQrCodeMutate={useQrCodeMutate}
        onClickRegenerateQrCode={regenerateQrCode}
        isQrExpired={isQrExpired}
        countDownDisplay={countDownDisplay}
      />

      <PaymentErrorModal
        closeAllModels={{
          isErrorModalOpen,
          callback: closePaymentModals,
        }}
        isRetry={isRetry}
        modelOpen={isErrorModalOpen}
        onClickModalButton={() => {
          onPaymentComplete(!isRetry);
          closeAllPaymentModals(isRetry || false);
        }}
        errorMessages={errorMessages}
        uniqueLink={isUniqueLink}
        buttonText={buttonErrorText}
      />
      <PaymentModals
        isConfirmationModalOpen={isConfirmationModalOpen}
        setIsConfirmationModalOpen={setIsConfirmationModalOpen}
        customConfirmationModalContent={customConfirmationModalContent}
        onHandlePaymentMethod={onHandlePaymentMethod}
        offlinePaymentMutate={offlinePaymentMutate}
        coordinatorExamPaymentMode={coordinatorExamPaymentMode}
        zeroDollarPaymentOption={zeroDollarPaymentOption}
        setIsPaymentModalOpen={setIsPaymentModalOpen}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        onClickPaymentButton={onClickPaymentButton}
        onSuccessPayment={onSuccessPayment}
        paymentErrorDispatch={paymentErrorDispatch}
        statusPaymentOffline={statusPaymentOffline}
        buttonText={buttonText}
        fullWidth={fullWidth}
        buttonVariant={buttonVariant}
        setIsDisabled={setIsDisabled}
        UNEXPECTED_ERROR_ACTION={UNEXPECTED_ERROR_ACTION}
        disabled={disabled}
      />
    </>
  );
};

export default GroupExamRegistrationPaymentButton;
